import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://karigarihubindia.com";
const DEFAULT_IMAGE = "https://karigarihubindia.com/images/embroidery/zardozi1.jpg";
const SITE_NAME = "KarigariHub";

function setMeta(attr, key, content) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export default function useSEO({ title, description, keywords, path, aiDescription, jsonLd, faqData }) {
  const location = useLocation();
  const url = `${BASE_URL}${path || location.pathname}`;

  useEffect(() => {
    document.title = title;

    // Standard SEO
    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);

    // Open Graph
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("property", "og:image", DEFAULT_IMAGE);
    setMeta("property", "og:site_name", SITE_NAME);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:locale", "en_IN");

    // Twitter
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);

    // AI / LLM platforms (ChatGPT, Gemini, Perplexity, Bing Chat)
    if (aiDescription) {
      setMeta("name", "ai:description", aiDescription);
      setMeta("name", "page-topic", aiDescription.substring(0, 150));
    }

    // Bing specific
    setMeta("name", "revisit-after", "7 days");
    setMeta("name", "rating", "general");

    // Canonical
    setCanonical(url);

    // Dynamic JSON-LD per page
    if (jsonLd) {
      setJsonLd("dynamic-page-jsonld", jsonLd);
    }

    // FAQ Schema for Google rich snippets
    if (faqData && faqData.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqData.map(faq => ({
          "@type": "Question",
          "name": faq.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.a
          }
        }))
      };
      setJsonLd("faq-jsonld", faqSchema);
    }

    // BreadcrumbList for non-home pages
    const pathName = path || location.pathname;
    if (pathName !== "/") {
      const segments = pathName.split("/").filter(Boolean);
      const breadcrumbs = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": BASE_URL },
          ...segments.map((seg, i) => ({
            "@type": "ListItem",
            "position": i + 2,
            "name": seg.charAt(0).toUpperCase() + seg.slice(1).replace(/-/g, " "),
            "item": `${BASE_URL}/${segments.slice(0, i + 1).join("/")}`
          }))
        ]
      };
      setJsonLd("breadcrumb-jsonld", breadcrumbs);
    }
  }, [title, description, keywords, url, aiDescription, jsonLd, faqData, path, location.pathname]);
}
